import { Drawer } from 'vaul'
import { Eye, Share2, Trophy, LogOut, Coffee, DollarSign, MessageCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import { tapScale } from '@/lib/motion'

interface ChatMessage {
  user: string
  msg: string
  time: string
}

interface MenuDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isCompact: boolean
  // Room info
  stakes: string
  gameType?: string
  spectatorCount: number
  // Player state
  isSpectator: boolean
  isSittingOut: boolean
  jackpotOptIn: boolean
  jackpotAmount: number
  playerStack: number
  // Chat
  chatMessages: ChatMessage[]
  // Callbacks
  onToggleSitOut: () => void
  onRebuy: () => void
  onToggleJackpot: () => void
  onJackpotInfo: () => void
  onShare: () => void
  onLeave: () => void
}

export function MenuDrawer({
  open,
  onOpenChange,
  isCompact,
  stakes,
  gameType = '無限注德州撲克',
  spectatorCount,
  isSpectator,
  isSittingOut,
  jackpotOptIn,
  jackpotAmount,
  playerStack,
  chatMessages,
  onToggleSitOut,
  onRebuy,
  onToggleJackpot,
  onJackpotInfo,
  onShare,
  onLeave,
}: MenuDrawerProps) {
  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
        <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
          <div className={cn(
            'bg-[#0a0c10] rounded-t-3xl border-t border-white/10',
            isCompact ? 'max-h-[95vh]' : 'max-h-[85vh]'
          )}>
            <div className={cn('flex justify-center', isCompact ? 'py-1.5' : 'py-3')}>
              <div className="w-10 h-1.5 rounded-full bg-white/20" />
            </div>
            <div className={cn(
              'overflow-y-auto safe-area-bottom scrollbar-hide',
              isCompact ? 'px-4 pb-3 max-h-[calc(95vh-24px)]' : 'px-6 pb-6 max-h-[calc(85vh-36px)]'
            )}>
              <Drawer.Title className={cn(
                'font-display font-semibold text-foreground mb-1',
                isCompact ? 'text-lg' : 'text-xl'
              )}>
                牌桌選單
              </Drawer.Title>
              <Drawer.Description className="text-xs text-muted-foreground mb-4">
                ${stakes} · {gameType}
              </Drawer.Description>

              {/* Room info bar */}
              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-elevated">
                  <Eye className="w-3.5 h-3.5 text-gold" />
                  <span className="text-xs font-bold text-foreground">{spectatorCount}</span>
                  <span className="text-[10px] text-muted-foreground">觀戰</span>
                </div>
                {!isSpectator && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-elevated">
                    <DollarSign className="w-3.5 h-3.5 text-gold" />
                    <span className="text-xs font-mono font-bold text-foreground">${(playerStack / 100).toLocaleString()}</span>
                  </div>
                )}
              </div>

              {/* Action items */}
              <div className={cn('space-y-1', isCompact ? 'mb-3' : 'mb-4')}>
                {/* Jackpot */}
                <button
                  onClick={onJackpotInfo}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Trophy className="w-4 h-4 text-gold" />
                    <div className="text-left">
                      <span className="text-sm font-medium text-foreground block">累積獎金</span>
                      <span className="text-xs font-mono font-bold text-gradient-gold-shimmer">${jackpotAmount.toLocaleString()}</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); onToggleJackpot() }}
                    className={cn(
                      'w-12 h-6 rounded-full transition-all duration-200 relative flex-shrink-0',
                      jackpotOptIn ? 'bg-gold' : 'bg-surface-elevated border border-white/10'
                    )}
                  >
                    <div className={cn(
                      'w-5 h-5 rounded-full bg-white shadow-md absolute top-0.5 transition-all duration-200',
                      jackpotOptIn ? 'left-6' : 'left-0.5'
                    )} />
                  </button>
                </button>

                {/* Sit out (only when seated) */}
                {!isSpectator && (
                  <button
                    onClick={onToggleSitOut}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Coffee className={cn('w-4 h-4', isSittingOut ? 'text-amber-400' : 'text-muted-foreground')} />
                      <span className="text-sm font-medium text-foreground">暫離座位</span>
                    </div>
                    <div className={cn(
                      'w-12 h-6 rounded-full transition-all duration-200 relative',
                      isSittingOut ? 'bg-amber-500' : 'bg-surface-elevated border border-white/10'
                    )}>
                      <div className={cn(
                        'w-5 h-5 rounded-full bg-white shadow-md absolute top-0.5 transition-all duration-200',
                        isSittingOut ? 'left-6' : 'left-0.5'
                      )} />
                    </div>
                  </button>
                )}

                {/* Rebuy (only when seated) */}
                {!isSpectator && (
                  <button
                    onClick={onRebuy}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 transition-colors"
                  >
                    <DollarSign className="w-4 h-4 text-gold" />
                    <span className="text-sm font-medium text-foreground">補碼</span>
                  </button>
                )}

                {/* Share */}
                <button
                  onClick={onShare}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 transition-colors"
                >
                  <Share2 className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">分享牌桌</span>
                </button>
              </div>

              {/* Chat history */}
              {chatMessages.length > 0 && (
                <div className={cn(isCompact ? 'mb-3' : 'mb-4')}>
                  <div className="flex items-center gap-2 mb-2">
                    <MessageCircle className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">聊天紀錄</span>
                  </div>
                  <div className="max-h-32 overflow-y-auto rounded-xl bg-surface-elevated p-3 space-y-1.5 scrollbar-hide">
                    {chatMessages.map((m, i) => (
                      <div key={i} className="text-xs">
                        <span className="font-bold text-gold">{m.user}</span>
                        <span className="text-muted-foreground/40 mx-1">·</span>
                        <span className="text-muted-foreground/40">{m.time}</span>
                        <p className="text-foreground/80">{m.msg}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Leave button */}
              {!isSpectator && (
                <motion.button
                  whileTap={tapScale}
                  onClick={onLeave}
                  className={cn(
                    'w-full rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-400 font-bold flex items-center justify-center gap-2 transition-colors hover:bg-rose-500/20',
                    isCompact ? 'h-10 text-sm' : 'h-12 text-base'
                  )}
                >
                  <LogOut className="w-4 h-4" />
                  離開牌桌
                </motion.button>
              )}
            </div>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}
